import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Ban,
  Check,
  Eye,
  EyeOff,
  KeyRound,
  Plus,
  RotateCw,
  Save,
  Search,
  ShieldCheck,
  Trash2,
  UserCog,
} from 'lucide-react';
import toast from 'react-hot-toast';

import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { Toggle } from '@/components/ui/Toggle';
import {
  createUser,
  deleteUser,
  getPermissions,
  listEstablishments,
  listUsers,
  resetUserPassword,
  setPermissions,
  setUserStatus,
  updateUser,
  type AdminUser,
  type PermissionRole,
  type PermissionsMatrix,
} from '@/services/admin';
import { useAuthStore } from '@/store/auth.store';
import { useEstablishmentStore } from '@/store/establishment.store';
import { formatDate, roleLabels } from '@/utils/format';

type Form = {
  id?: string;
  email: string;
  phone: string;
  name: string;
  role: AdminUser['role'];
  password: string;
  establishmentId: string;
  maxDiscountPercent: number;
};

const emptyForm: Form = {
  email: '',
  phone: '',
  name: '',
  role: 'CASHIER',
  password: '',
  establishmentId: '',
  maxDiscountPercent: 0,
};

export default function UsersPage() {
  const qc = useQueryClient();
  // /users?view=roles — сразу «Права ролей» (ссылка из «Устройства → Официанты»).
  const [view, setView] = useState<'users' | 'roles'>(() =>
    new URLSearchParams(window.location.search).get('view') === 'roles' ? 'roles' : 'users',
  );

  const [q, setQ] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'active' | 'blocked' | 'all'>('active');

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<Form>(emptyForm);

  const [pwTarget, setPwTarget] = useState<AdminUser | null>(null);
  const [delTarget, setDelTarget] = useState<AdminUser | null>(null);

  const statusQuery =
    statusFilter === 'active' ? 'ACTIVE' : statusFilter === 'blocked' ? 'BLOCKED' : undefined;

  const { data, isLoading } = useQuery({
    queryKey: ['admin/users', { q, roleFilter, statusQuery }],
    queryFn: () =>
      listUsers({
        q: q || undefined,
        role: roleFilter || undefined,
        status: statusQuery,
      }),
  });

  const { data: establishments } = useQuery({
    queryKey: ['admin/establishments'],
    queryFn: () => listEstablishments(),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin/users'] });
  };

  const createMut = useMutation({
    mutationFn: () => createUser(buildPayload(form, true)),
    onSuccess: () => {
      toast.success('Пользователь создан');
      setShowForm(false);
      setForm(emptyForm);
      invalidate();
    },
  });

  const updateMut = useMutation({
    mutationFn: () => {
      if (!form.id) throw new Error('Нет id');
      return updateUser(form.id, buildPayload(form, false));
    },
    onSuccess: () => {
      toast.success('Сохранено');
      setShowForm(false);
      setForm(emptyForm);
      invalidate();
    },
  });

  const statusMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'ACTIVE' | 'BLOCKED' }) =>
      setUserStatus(id, status),
    onSuccess: (_res, vars) => {
      toast.success(vars.status === 'ACTIVE' ? 'Разблокирован' : 'Заблокирован');
      invalidate();
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteUser(id),
    onSuccess: () => {
      toast.success('Удалён');
      setDelTarget(null);
      invalidate();
    },
  });

  const pwMut = useMutation({
    mutationFn: ({ id, pw }: { id: string; pw: string }) => resetUserPassword(id, pw),
    onSuccess: () => {
      toast.success('Пароль изменён');
      setPwTarget(null);
    },
  });

  const openNew = () => {
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (u: AdminUser) => {
    setForm({
      id: u.id,
      email: u.email ?? '',
      phone: u.phone ?? '',
      name: u.name ?? '',
      role: u.role,
      password: '',
      establishmentId: u.establishment?.id ?? '',
      maxDiscountPercent: u.maxDiscountPercent ?? 0,
    });
    setShowForm(true);
  };

  const isEdit = Boolean(form.id);

  // Пароль обязателен только при создании КАССИРА (он логинится в приложении
  // кассы по email/телефон + пароль). ADMIN/OWNER входят в админку по коду из
  // письма, WAITER — по коду из «Устройства → Официанты» или из письма,
  // CLIENT — по OTP, им пароль не нужен.
  const needsPassword = !isEdit && form.role === 'CASHIER';
  // Для входа по коду нужен именно email (OTP шлётся на почту).
  const needsEmail = form.role === 'ADMIN' || form.role === 'OWNER';
  // Официанту, подключённому кодом, почта не нужна — но без неё нужно имя.
  const hasIdentity =
    form.role === 'WAITER'
      ? form.name.trim() || form.email.trim()
      : needsEmail
        ? form.email.trim()
        : form.email.trim() || form.phone.trim();

  const canSubmit =
    hasIdentity &&
    (!needsPassword || form.password.length >= 4) &&
    !createMut.isPending &&
    !updateMut.isPending;

  const visibleRows = useMemo(() => data?.results ?? [], [data?.results]);

  return (
    <div>
      <header className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-semibold">Пользователи</h1>
          <p className="text-sm text-slate-500">Админы, владельцы, кассиры, официанты, клиенты</p>
        </div>
        {view === 'users' && (
          <button className="btn-primary shrink-0" onClick={openNew}>
            <Plus size={16} /> <span className="hidden sm:inline">Добавить</span>
          </button>
        )}
      </header>

      <div className="mb-4 inline-flex rounded-md border border-slate-200 overflow-hidden text-sm">
        {([['users', 'Пользователи'], ['roles', 'Права ролей']] as const).map(([id, label]) => (
          <button
            key={id}
            onClick={() => setView(id)}
            className={`px-3 py-1.5 ${view === id ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {view === 'roles' && <RolesTab />}

      {view === 'users' && (
      <>
      <div className="flex flex-wrap gap-2 sm:gap-3 mb-4 items-center">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <input
            className="input pl-9"
            placeholder="Поиск по имени, email, телефону…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <select
          className="input w-full sm:w-48"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
        >
          <option value="">Все роли</option>
          <option value="ADMIN">Админ</option>
          <option value="OWNER">Владелец</option>
          <option value="CASHIER">Кассир</option>
          <option value="WAITER">Официант</option>
          <option value="CLIENT">Клиент</option>
        </select>
        <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-sm">
          {(['active', 'blocked', 'all'] as const).map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 ${statusFilter === s ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
            >
              {s === 'active' ? 'Активные' : s === 'blocked' ? 'Заблокир.' : 'Все'}
            </button>
          ))}
        </div>
        <button
          className="btn-ghost"
          onClick={() => qc.invalidateQueries({ queryKey: ['admin/users'] })}
          title="Обновить"
        >
          <RotateCw size={14} />
        </button>
      </div>

      <div className="card overflow-x-auto">
        {isLoading ? (
          <Spinner label="Загружаем пользователей" />
        ) : (
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-xs uppercase">
                <th className="text-left px-4 py-3">Имя</th>
                <th className="text-left px-4 py-3">Контакты</th>
                <th className="text-left px-4 py-3">Роль</th>
                <th className="text-left px-4 py-3">Статус</th>
                <th className="text-left px-4 py-3">Заведение</th>
                <th className="text-right px-4 py-3">Действия</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((u) => {
                const isDeleted = u.status === 'DELETED';
                return (
                  <tr key={u.id} className={`table-row ${isDeleted ? 'opacity-60' : ''}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium">{u.name ?? '—'}</div>
                      <div className="text-xs text-slate-400">
                        {formatDate(u.createdAt, { hour: undefined, minute: undefined })}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {u.email && <div>{u.email}</div>}
                      {u.phone && <div className="text-slate-500">{u.phone}</div>}
                      {!u.email && !u.phone && <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-3">{roleLabels[u.role] ?? u.role}</td>
                    <td className="px-4 py-3">
                      {u.status === 'ACTIVE' && <Badge tone="success">Активен</Badge>}
                      {u.status === 'BLOCKED' && <Badge tone="warning">Заблокирован</Badge>}
                      {u.status === 'DELETED' && <Badge tone="danger">Удалён</Badge>}
                    </td>
                    <td className="px-4 py-3">{u.establishment?.name ?? '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          className="btn-ghost"
                          onClick={() => openEdit(u)}
                          title="Редактировать"
                          disabled={isDeleted}
                        >
                          <UserCog size={16} />
                        </button>
                        <button
                          className="btn-ghost"
                          title="Сменить пароль"
                          onClick={() => setPwTarget(u)}
                          disabled={isDeleted}
                        >
                          <KeyRound size={16} />
                        </button>
                        {u.status === 'ACTIVE' ? (
                          <button
                            className="btn-ghost text-amber-600"
                            title="Заблокировать"
                            onClick={() => statusMut.mutate({ id: u.id, status: 'BLOCKED' })}
                            disabled={statusMut.isPending}
                          >
                            <Ban size={16} />
                          </button>
                        ) : u.status === 'BLOCKED' ? (
                          <button
                            className="btn-ghost text-emerald-600"
                            title="Разблокировать"
                            onClick={() => statusMut.mutate({ id: u.id, status: 'ACTIVE' })}
                            disabled={statusMut.isPending}
                          >
                            <Check size={16} />
                          </button>
                        ) : (
                          <button
                            className="btn-ghost text-emerald-600"
                            title="Восстановить (активировать)"
                            onClick={() => statusMut.mutate({ id: u.id, status: 'ACTIVE' })}
                            disabled={statusMut.isPending}
                          >
                            <Check size={16} />
                          </button>
                        )}
                        {!isDeleted && (
                          <button
                            className="btn-ghost text-red-600"
                            title="Удалить"
                            onClick={() => setDelTarget(u)}
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {visibleRows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-400">
                    Ничего не найдено
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Форма создания / редактирования ── */}
      <Modal
        open={showForm}
        onClose={() => setShowForm(false)}
        title={isEdit ? 'Редактировать пользователя' : 'Новый пользователь'}
        footer={
          <>
            <button className="btn-outline" onClick={() => setShowForm(false)}>
              Отмена
            </button>
            <button
              className="btn-primary"
              disabled={!canSubmit}
              onClick={() => (isEdit ? updateMut.mutate() : createMut.mutate())}
            >
              {createMut.isPending || updateMut.isPending
                ? 'Сохраняем…'
                : isEdit
                  ? 'Сохранить'
                  : 'Создать'}
            </button>
          </>
        }
      >
        <UserForm
          form={form}
          setForm={setForm}
          isEdit={isEdit}
          establishments={establishments ?? []}
        />
      </Modal>

      {/* ── Смена пароля ── */}
      <PasswordModal
        user={pwTarget}
        onClose={() => setPwTarget(null)}
        isPending={pwMut.isPending}
        onSubmit={(pw) => pwTarget && pwMut.mutate({ id: pwTarget.id, pw })}
      />

      {/* ── Подтверждение удаления ── */}
      <DeleteConfirmModal
        user={delTarget}
        onClose={() => setDelTarget(null)}
        isPending={deleteMut.isPending}
        onConfirm={() => delTarget && deleteMut.mutate(delTarget.id)}
      />
      </>
      )}
    </div>
  );
}

// ── Права ролей (п.17) ──
// Владелец включает/выключает права для ролей «Админ», «Кассир» и «Официант»
// текущего заведения. Хранится в Establishment.settings.rolePermissions; касса
// получает список в device-config, телефон официанта — в /me, сервер проверяет
// на критичных ручках.

const ROLE_TABS: Array<{ id: PermissionRole; label: string; hint: string }> = [
  { id: 'ADMIN', label: 'Администратор', hint: 'Сотрудник с доступом в админку и кассу' },
  { id: 'CASHIER', label: 'Кассир', hint: 'Работает только в приложении кассы' },
  { id: 'WAITER', label: 'Официант', hint: 'Телефон: меню стола по долгому нажатию' },
];

function RolesTab() {
  const qc = useQueryClient();
  const myRole = useAuthStore((s) => s.user?.role);
  const estId = useEstablishmentStore((s) => s.currentId);
  const canEdit = myRole === 'OWNER';

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin/permissions', estId],
    queryFn: getPermissions,
  });

  const [role, setRole] = useState<PermissionRole>(() => {
    const fromUrl = new URLSearchParams(window.location.search).get('role');
    return fromUrl === 'WAITER' || fromUrl === 'ADMIN' ? fromUrl : 'CASHIER';
  });
  const [draft, setDraft] = useState<Set<string>>(new Set());

  const savedList = data?.roles[role]?.permissions ?? [];
  useEffect(() => { setDraft(new Set(savedList)); }, [role, data]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = useMemo(() => {
    if (savedList.length !== draft.size) return true;
    return savedList.some((c) => !draft.has(c));
  }, [savedList, draft]);

  const saveMut = useMutation({
    mutationFn: (body: Partial<Record<PermissionRole, string[] | null>>) => setPermissions(body),
    onSuccess: (next) => {
      qc.setQueryData(['admin/permissions', estId], next);
      toast.success('Права сохранены. Кассы и телефоны официантов применят их при следующем открытии приложения');
    },
  });

  if (isLoading) return <Spinner label="Загружаем права" />;
  if (error || !data) {
    return (
      <div className="card p-6 text-sm text-slate-500">
        Права ролей доступны, когда выбрано заведение.
      </div>
    );
  }

  const groups = groupCodes(data, role);
  const isDefault = !data.roles[role]?.customized;
  // Старый бэкенд не знает роль официанта — вкладку не показываем.
  const roleTabs = ROLE_TABS.filter((r) => r.id !== 'WAITER' || data.roles.WAITER);

  return (
    <div className="grid gap-4 lg:grid-cols-[220px_1fr]">
      <div className="card p-2 self-start">
        {roleTabs.map((r) => (
          <button
            key={r.id}
            onClick={() => setRole(r.id)}
            className={`w-full rounded-md px-3 py-2 text-left transition-colors ${
              role === r.id ? 'bg-slate-900 text-white' : 'hover:bg-slate-50'
            }`}
          >
            <div className="text-sm font-medium">{r.label}</div>
            <div className={`text-xs ${role === r.id ? 'text-slate-300' : 'text-slate-500'}`}>{r.hint}</div>
          </button>
        ))}
        <div className="mt-3 px-3 pb-1 text-xs text-slate-400">
          Владелец имеет все права всегда — их нельзя ограничить.
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-5 py-3">
          <div className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-slate-500" />
            <span className="font-semibold">{ROLE_TABS.find((r) => r.id === role)?.label}</span>
            {isDefault ? <Badge>по умолчанию</Badge> : <Badge tone="info">настроено</Badge>}
          </div>
          {canEdit && !isDefault && !dirty && (
            <button
              className="btn-ghost text-xs"
              disabled={saveMut.isPending}
              onClick={() => saveMut.mutate({ [role]: null })}
            >
              <RotateCw size={14} /> Сбросить на стандартные
            </button>
          )}
        </div>

        {!canEdit && (
          <div className="mx-5 mt-4 flex items-center gap-2 rounded-md bg-amber-50 p-3 text-xs text-amber-700">
            <AlertTriangle size={14} />
            Менять права может только владелец заведения. Вы видите текущие настройки.
          </div>
        )}

        <div className="divide-y divide-slate-100">
          {groups.map(([group, codes]) => (
            <section key={group} className="px-5 py-4">
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{group}</h3>
              <div className="divide-y divide-slate-50">
                {codes.map((c) => {
                  const on = draft.has(c.code);
                  const byDefault = data.defaults[role]?.includes(c.code) ?? false;
                  return (
                    <div key={c.code} className="flex items-center justify-between gap-4 py-2.5">
                      <div className="min-w-0">
                        <div className="text-sm">{c.title}</div>
                        <div className="text-xs text-slate-400">
                          <code className="font-mono">{c.code}</code>
                          {byDefault ? ' · стандартно включено' : ' · стандартно выключено'}
                        </div>
                      </div>
                      <Toggle
                        checked={on}
                        disabled={!canEdit || saveMut.isPending}
                        onChange={(v) =>
                          setDraft((d) => {
                            const n = new Set(d);
                            if (v) n.add(c.code); else n.delete(c.code);
                            return n;
                          })
                        }
                        label={c.title}
                      />
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        {canEdit && dirty && (
          <div className="sticky bottom-0 flex items-center justify-between gap-3 border-t border-slate-100 bg-white/95 px-5 py-3 backdrop-blur">
            <span className="text-xs text-slate-500">Есть несохранённые изменения</span>
            <div className="flex gap-2">
              <button className="btn-ghost" disabled={saveMut.isPending} onClick={() => setDraft(new Set(savedList))}>
                Отменить
              </button>
              <button
                className="btn-primary"
                disabled={saveMut.isPending}
                onClick={() => saveMut.mutate({ [role]: Array.from(draft) })}
              >
                <Save size={16} /> {saveMut.isPending ? 'Сохраняем…' : 'Сохранить'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Только права, которые у роли имеют смысл: оплаты с телефона у кассира нет, а
// скидок и склада — у официанта.
function groupCodes(m: PermissionsMatrix, role: PermissionRole): Array<[string, PermissionsMatrix['codes']]> {
  const map = new Map<string, PermissionsMatrix['codes']>();
  for (const c of m.codes) {
    if (c.roles && !c.roles.includes(role)) continue;
    const list = map.get(c.group);
    if (list) list.push(c); else map.set(c.group, [c]);
  }
  return Array.from(map.entries());
}

// ── Форма ──

function UserForm({
  form,
  setForm,
  isEdit,
  establishments,
}: {
  form: Form;
  setForm: (f: Form) => void;
  isEdit: boolean;
  establishments: Array<{ id: string; name: string }>;
}) {
  const [showPw, setShowPw] = useState(false);

  const showEstablishment = isEstablishmentRole(form.role);

  return (
    <div className="grid gap-3">
      <div>
        <label className="label">Имя</label>
        <input
          className="input"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">Email</label>
          <input
            className="input"
            type="email"
            autoComplete="off"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </div>
        <div>
          <label className="label">Телефон</label>
          <input
            className="input"
            inputMode="tel"
            placeholder="+7 999 000 00 00"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">Роль</label>
          <select
            className="input"
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value as Form['role'] })}
          >
            <option value="CASHIER">Кассир</option>
            <option value="WAITER">Официант</option>
            <option value="ADMIN">Админ</option>
            <option value="OWNER">Владелец</option>
            <option value="CLIENT">Клиент</option>
          </select>
        </div>
        <div>
          <label className="label">
            {isEdit
              ? 'Новый пароль (необязательно)'
              : form.role === 'CASHIER'
                ? 'Пароль (мин. 4 символа)'
                : 'Пароль (необязательно)'}
          </label>
          <div className="relative">
            <input
              type={showPw ? 'text' : 'password'}
              className="input pr-9"
              autoComplete="new-password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              onClick={() => setShowPw((x) => !x)}
              tabIndex={-1}
            >
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>
      </div>
      {showEstablishment && (
        <div>
          <label className="label">Заведение</label>
          <select
            className="input"
            value={form.establishmentId}
            onChange={(e) => setForm({ ...form, establishmentId: e.target.value })}
          >
            <option value="">— без привязки —</option>
            {establishments.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
          <p className="text-xs text-slate-400 mt-1">
            {form.role === 'WAITER'
              ? 'Официант видит столы и меню этого заведения, заказы уходят на его кассу.'
              : 'Кассиру/админу заведение необходимо, чтобы видеть данные по смене.'}
          </p>
        </div>
      )}
      {form.role === 'CASHIER' && (
        <div>
          <label className="label">Максимальная скидка (%)</label>
          <input
            className="input"
            type="number"
            min={0}
            max={100}
            step={1}
            value={form.maxDiscountPercent}
            onChange={(e) => {
              const n = Math.max(0, Math.min(100, Math.round(Number(e.target.value) || 0)));
              setForm({ ...form, maxDiscountPercent: n });
            }}
          />
          <p className="text-xs text-slate-400 mt-1">
            Персональный лимит. Если в Настройки → Касса задан общий лимит скидки кассира,
            он имеет приоритет и это поле не учитывается.
          </p>
        </div>
      )}
      <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-3 space-y-1">
        {(form.role === 'ADMIN' || form.role === 'OWNER') && (
          <p>
            Вход в <b>админку</b> — по коду из письма. Обязательно укажите <b>email</b>,
            пароль не требуется.
          </p>
        )}
        {form.role === 'CASHIER' && (
          <p>
            Вход в <b>приложении кассы</b> — по <b>email или телефону</b> + <b>пароль</b>.
          </p>
        )}
        {form.role === 'WAITER' && (
          <p>
            Телефон официанта подключают кодом в <b>Устройства → Официанты</b> — почта не
            нужна, достаточно имени. С <b>email</b> официант может войти и по коду из письма.
            Пароль не требуется.
          </p>
        )}
        {form.role === 'CLIENT' && (
          <p>
            Клиент входит в приложении по коду из SMS/письма — пароль не нужен.
          </p>
        )}
      </div>
    </div>
  );
}

// ── Модалка смены пароля ──

function PasswordModal({
  user,
  onClose,
  onSubmit,
  isPending,
}: {
  user: AdminUser | null;
  onClose: () => void;
  onSubmit: (pw: string) => void;
  isPending: boolean;
}) {
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [show, setShow] = useState(false);

  const ok = pw.length >= 4 && pw === pw2 && !isPending;

  const close = () => {
    setPw('');
    setPw2('');
    setShow(false);
    onClose();
  };

  return (
    <Modal
      open={!!user}
      onClose={close}
      title="Смена пароля"
      size="sm"
      footer={
        <>
          <button className="btn-outline" onClick={close}>
            Отмена
          </button>
          <button
            className="btn-primary"
            disabled={!ok}
            onClick={() => onSubmit(pw)}
          >
            {isPending ? 'Сохраняем…' : 'Сохранить'}
          </button>
        </>
      }
    >
      {user && (
        <div className="space-y-3 text-sm">
          <p>
            Новый пароль для{' '}
            <span className="font-medium">{user.name ?? user.email ?? user.phone ?? user.id}</span>
          </p>
          <div>
            <label className="label">Новый пароль (мин. 4 символа)</label>
            <div className="relative">
              <input
                type={show ? 'text' : 'password'}
                className="input pr-9"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                autoFocus
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                onClick={() => setShow((x) => !x)}
                tabIndex={-1}
              >
                {show ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div>
            <label className="label">Повторите пароль</label>
            <input
              type={show ? 'text' : 'password'}
              className="input"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
            />
            {pw2 && pw !== pw2 && (
              <p className="text-xs text-rose-600 mt-1">Пароли не совпадают</p>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

// ── Модалка подтверждения удаления ──

function DeleteConfirmModal({
  user,
  onClose,
  onConfirm,
  isPending,
}: {
  user: AdminUser | null;
  onClose: () => void;
  onConfirm: () => void;
  isPending: boolean;
}) {
  return (
    <Modal
      open={!!user}
      onClose={onClose}
      title="Удалить пользователя?"
      size="sm"
      footer={
        <>
          <button className="btn-outline" onClick={onClose}>
            Отмена
          </button>
          <button
            className="btn-danger inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-rose-600 hover:bg-rose-700 text-white text-sm disabled:opacity-50"
            disabled={isPending}
            onClick={onConfirm}
          >
            <Trash2 size={14} /> {isPending ? 'Удаляем…' : 'Удалить'}
          </button>
        </>
      }
    >
      {user && (
        <div className="space-y-3 text-sm">
          <div className="flex items-center gap-2 text-amber-700 bg-amber-50 p-3 rounded-md">
            <AlertTriangle size={16} />
            <span>Пользователь будет помечен как удалённый. Его нельзя будет авторизовать.</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Row label="Имя" value={user.name ?? '—'} />
            <Row label="Роль" value={roleLabels[user.role] ?? user.role} />
            <Row label="Email" value={user.email ?? '—'} />
            <Row label="Телефон" value={user.phone ?? '—'} />
          </div>
          <p className="text-xs text-slate-400">
            Позднее можно восстановить: переведя статус обратно в «Активен».
          </p>
        </div>
      )}
    </Modal>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="font-medium truncate">{value}</div>
    </div>
  );
}

// ── helpers ──

// Сотрудники привязаны к заведению; у клиента привязки нет.
function isEstablishmentRole(role: Form['role']): boolean {
  return role === 'CASHIER' || role === 'OWNER' || role === 'ADMIN' || role === 'WAITER';
}

function buildPayload(form: Form, isCreate: boolean) {
  const payload: Partial<AdminUser> & { password?: string; establishmentId?: string | null } = {
    role: form.role,
    name: form.name.trim() || null,
    email: form.email.trim().toLowerCase() || null,
    phone: form.phone.trim() || null,
  };
  if (form.password) payload.password = form.password;
  if (isEstablishmentRole(form.role)) {
    payload.establishmentId = form.establishmentId || null;
  } else if (isCreate === false) {
    payload.establishmentId = null;
  }
  if (form.role === 'CASHIER') {
    payload.maxDiscountPercent = Math.max(0, Math.min(100, Math.round(form.maxDiscountPercent || 0)));
  }
  return payload;
}
